import { useEffect, useRef, useState } from "react";
import {
  Badge,
  CompanyProfileDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Progress,
  Textarea,
  VendorAvatar,
} from "@semo/shared-ui";
import { BadgeCheck, Camera, Eye, Loader2, Plus, Trash2 } from "@semo/icons";
import { toast } from "sonner";

import {
  useAddCertification,
  useDeleteCertification,
  useMyCompanyProfile,
  useUpdateMyCompanyProfile,
  useUploadCompanyLogo,
} from "../hooks/useMyCompanyProfile";
import { withObjectParticle } from "../koreanParticle";

/** 완성도 게이지가 조르는 항목의 한국어 이름 */
const MISSING_LABELS: Record<string, string> = {
  logoUrl: "회사 로고",
  homepageUrl: "홈페이지",
  establishedAt: "설립일",
  companyIntro: "회사 소개",
  productDescription: "취급 품목",
  certifications: "인증서",
};

const PENDING_BADGE = {
  label: "검증 대기",
  className: "bg-amber-50 text-amber-700",
};
const CERT_STATUS_BADGE: Record<string, { label: string; className: string }> =
  {
    PENDING: PENDING_BADGE,
    VERIFIED: { label: "검증됨", className: "bg-emerald-50 text-emerald-700" },
    REJECTED: { label: "반려됨", className: "bg-red-50 text-red-600" },
  };

interface MyCompanyProfileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 판매자 센터 «내 기업 프로필» 시트.
 *
 * 구매자에게 보이는 프로필을 미리 보고, 비어 있는 항목(홈페이지·설립일·
 * 소개·인증서)을 여기서 채운다. 완성도 게이지가 수집 장치다.
 */
export function MyCompanyProfileSheet({
  open,
  onOpenChange,
}: MyCompanyProfileSheetProps) {
  const { data: profile, isLoading } = useMyCompanyProfile({ enabled: open });
  const updateProfile = useUpdateMyCompanyProfile();
  const uploadLogo = useUploadCompanyLogo();
  const addCertification = useAddCertification();
  const deleteCertification = useDeleteCertification();

  const [previewOpen, setPreviewOpen] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // 편집 폼 상태 — 프로필이 로드되면 채운다
  const [homepageUrl, setHomepageUrl] = useState("");
  const [establishedAt, setEstablishedAt] = useState("");
  const [companyIntro, setCompanyIntro] = useState("");
  const [productDescription, setProductDescription] = useState("");

  // 인증서 추가 폼
  const [certFormOpen, setCertFormOpen] = useState(false);
  const [certName, setCertName] = useState("");
  const [certIssuer, setCertIssuer] = useState("");
  const [certIssuedAt, setCertIssuedAt] = useState("");
  const [certExpiresAt, setCertExpiresAt] = useState("");
  const [certFile, setCertFile] = useState<File | null>(null);

  useEffect(() => {
    if (!profile) return;
    setHomepageUrl(profile.homepageUrl ?? "");
    setEstablishedAt(profile.establishedAt ?? "");
    setCompanyIntro(profile.companyIntro ?? "");
    setProductDescription(profile.productDescription ?? "");
  }, [profile]);

  const handleSave = async () => {
    try {
      await updateProfile.mutateAsync({
        homepageUrl,
        establishedAt: establishedAt || undefined,
        companyIntro,
        productDescription,
      });
      toast.success("기업 프로필이 저장되었습니다.");
    } catch {
      toast.error("프로필 저장에 실패했습니다.");
    }
  };

  const handleLogoChange = async (file: File | undefined) => {
    if (!file) return;
    try {
      await uploadLogo.mutateAsync(file);
      toast.success("로고가 업로드되었습니다.");
    } catch {
      toast.error("로고 업로드에 실패했습니다.");
    }
  };

  const handleAddCertification = async () => {
    if (!certName.trim()) {
      toast.error("인증서 명칭을 입력해 주세요.");
      return;
    }
    try {
      await addCertification.mutateAsync({
        name: certName.trim(),
        issuer: certIssuer.trim() || undefined,
        issuedAt: certIssuedAt || undefined,
        expiresAt: certExpiresAt || undefined,
        file: certFile,
      });
      toast.success("인증서가 등록되었습니다. 검증 후 프로필에 노출됩니다.");
      setCertFormOpen(false);
      setCertName("");
      setCertIssuer("");
      setCertIssuedAt("");
      setCertExpiresAt("");
      setCertFile(null);
    } catch {
      toast.error("인증서 등록에 실패했습니다.");
    }
  };

  const missingLabels =
    profile?.completeness.missing
      .map((key) => MISSING_LABELS[key])
      .filter(Boolean) ?? [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>내 기업 프로필</DialogTitle>
            <DialogDescription>
              구매자가 견적 카드와 채팅에서 보는 우리 회사의 얼굴입니다.
            </DialogDescription>
          </DialogHeader>

          {isLoading || !profile ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {/* 완성도 */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">
                    프로필 완성도
                  </p>
                  <p className="text-sm font-semibold tabular-nums text-primary">
                    {profile.completeness.percent}%
                  </p>
                </div>
                <Progress
                  value={profile.completeness.percent}
                  className="mt-2 h-2"
                />
                {missingLabels.length > 0 && (
                  <p className="mt-2 text-xs text-slate-500">
                    {withObjectParticle(missingLabels.join(" · "))} 채우면
                    신뢰도가 올라가요
                  </p>
                )}
              </div>

              {/* 로고 + 회사명 */}
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  className="group relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-slate-200"
                  onClick={() => logoInputRef.current?.click()}
                  aria-label="로고 변경"
                >
                  <VendorAvatar
                    name={profile.companyName ?? "공급사"}
                    imageUrl={profile.logoUrl}
                    className="h-full w-full rounded-none border-0 text-2xl"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                    <Camera className="h-5 w-5 text-white" />
                  </span>
                </button>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    void handleLogoChange(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-slate-900">
                    {profile.companyName}
                  </p>
                  <p className="text-xs text-slate-500">
                    {profile.ceoName ? `대표 ${profile.ceoName} · ` : ""}
                    {profile.region ?? ""}
                  </p>
                  {profile.trustPercentile != null && (
                    <span className="mt-1 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-2xs font-semibold text-primary-text">
                      신뢰도 상위 {profile.trustPercentile}%
                    </span>
                  )}
                </div>
              </div>

              {/* 편집 폼 */}
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="profile-homepage">홈페이지</Label>
                  <Input
                    id="profile-homepage"
                    placeholder="https://www.example.co.kr"
                    value={homepageUrl}
                    onChange={(e) => setHomepageUrl(e.target.value)}
                  />
                  <p className="text-2xs text-slate-400">
                    저장하면 홈페이지 대표 이미지가 프로필 썸네일로 걸려요
                  </p>
                </div>
                {profile.landingThumbnailUrl && (
                  <img
                    src={profile.landingThumbnailUrl}
                    alt="홈페이지 썸네일"
                    className="h-28 w-full rounded-xl border border-slate-200 object-cover"
                  />
                )}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="profile-established">설립일</Label>
                  <Input
                    id="profile-established"
                    type="date"
                    value={establishedAt}
                    onChange={(e) => setEstablishedAt(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="profile-intro">회사 소개</Label>
                  <Textarea
                    id="profile-intro"
                    rows={3}
                    placeholder="주요 사업 영역, 강점 등을 소개해 주세요"
                    value={companyIntro}
                    onChange={(e) => setCompanyIntro(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="profile-products">취급 품목</Label>
                  <Input
                    id="profile-products"
                    placeholder="예: 실험용 시약, 초자기구, 안전용품"
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value)}
                  />
                </div>
              </div>

              {/* 인증서 */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">
                    보유 인증서
                  </p>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    onClick={() => setCertFormOpen((v) => !v)}
                  >
                    <Plus className="h-3.5 w-3.5" /> 인증서 추가
                  </button>
                </div>

                {profile.certifications.length === 0 && !certFormOpen && (
                  <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                    ISO·벤처기업·이노비즈 등 인증서를 올리면 검증 후 프로필에
                    노출돼요.
                  </p>
                )}

                {profile.certifications.map((cert) => {
                  const badge = CERT_STATUS_BADGE[cert.status] ?? PENDING_BADGE;
                  const fromCmarket = cert.source === "CMARKET";
                  return (
                    <div
                      key={cert.id}
                      className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2"
                    >
                      <BadgeCheck className="h-4 w-4 shrink-0 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {cert.name}
                          {cert.issuer ? ` (${cert.issuer})` : ""}
                        </p>
                        {cert.status === "REJECTED" && cert.rejectionReason && (
                          <p className="truncate text-2xs text-red-500">
                            {cert.rejectionReason}
                          </p>
                        )}
                      </div>
                      {fromCmarket && (
                        <Badge className="shrink-0 border-0 bg-primary/5 text-primary-text">
                          씨마켓
                        </Badge>
                      )}
                      <Badge className={`shrink-0 border-0 ${badge.className}`}>
                        {badge.label}
                      </Badge>
                      {/* 씨마켓 동기화분은 원본이 씨마켓 프로필이다 — 여기서 못 지운다 */}
                      {!fromCmarket && (
                        <button
                          type="button"
                          aria-label="인증서 삭제"
                          className="shrink-0 text-slate-300 hover:text-red-500"
                          onClick={() => {
                            void deleteCertification
                              .mutateAsync(cert.id)
                              .then(() => toast.success("삭제되었습니다."))
                              .catch(() => toast.error("삭제에 실패했습니다."));
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}

                {certFormOpen && (
                  <div className="flex flex-col gap-2 rounded-xl border border-dashed border-slate-300 p-3">
                    <Input
                      placeholder="인증서 명칭 (예: ISO 9001) *"
                      value={certName}
                      onChange={(e) => setCertName(e.target.value)}
                    />
                    <Input
                      placeholder="발급 기관"
                      value={certIssuer}
                      onChange={(e) => setCertIssuer(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <div className="flex flex-1 flex-col gap-1">
                        <Label className="text-2xs text-slate-500">
                          발급일
                        </Label>
                        <Input
                          type="date"
                          value={certIssuedAt}
                          onChange={(e) => setCertIssuedAt(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-1 flex-col gap-1">
                        <Label className="text-2xs text-slate-500">
                          만료일
                        </Label>
                        <Input
                          type="date"
                          value={certExpiresAt}
                          onChange={(e) => setCertExpiresAt(e.target.value)}
                        />
                      </div>
                    </div>
                    <Input
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
                      onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
                    />
                    <button
                      type="button"
                      disabled={addCertification.isPending}
                      onClick={() => void handleAddCertification()}
                      className="mt-1 rounded-lg bg-primary py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                    >
                      {addCertification.isPending ? "등록 중..." : "등록"}
                    </button>
                  </div>
                )}
              </div>

              {/* 하단 액션 */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    // 같은 클릭이 새 다이얼로그의 outside-click 으로 잡히지 않도록
                    // 편집 시트를 먼저 닫고 다음 프레임에 미리보기를 연다
                    onOpenChange(false);
                    setTimeout(() => setPreviewOpen(true), 150);
                  }}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  <Eye className="h-4 w-4" /> 구매자 화면 미리보기
                </button>
                <button
                  type="button"
                  disabled={updateProfile.isPending}
                  onClick={() => void handleSave()}
                  className="flex-1 rounded-xl bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {updateProfile.isPending ? "저장 중..." : "저장"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 구매자에게 보이는 모습 (계약 후 = 전체 공개 기준). 닫으면 편집 시트로 복귀 */}
      <CompanyProfileDialog
        open={previewOpen}
        onOpenChange={(nextOpen) => {
          setPreviewOpen(nextOpen);
          if (!nextOpen) onOpenChange(true);
        }}
        profile={profile ?? null}
        isLoading={isLoading}
      />
    </>
  );
}
